import React from 'react';
import { createPortal } from 'react-dom';
import { fmt } from './oilApi';
import { OilIcon, KakoCoin } from './OilIcons';
import { sfxClose } from './oilSounds';

const Sec = ({ n, title, children, testid }) => (
  <div className="mt-2.5 rounded-xl border border-zinc-800 bg-black/50 p-3" data-testid={testid}>
    <p className="mb-1.5 flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.15em] text-amber-400">
      <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-amber-500 text-[9px] font-black text-black">{n}</span>
      {title}
    </p>
    <div className="space-y-1 text-[10px] leading-relaxed text-zinc-300">{children}</div>
  </div>
);

const Li = ({ children }) => (
  <p className="flex gap-1.5">
    <span className="shrink-0 text-amber-600">▸</span><span>{children}</span>
  </p>
);

export const OilRankRules = ({ kind, jackpot = {}, coinsPerAsset = 1000, payout = [], onClose }) => {
  const isMag = kind === 'magnates';
  const pot = Number((isMag ? jackpot.magnates : jackpot.shareholders) || 0);
  const split = (
    <div className="mt-2 overflow-hidden rounded-xl border border-amber-900/60" data-testid="oil-rank-prize-split">
      <div className="flex bg-amber-950/40 px-2 py-1 text-[8.5px] font-black uppercase tracking-[0.15em] text-amber-500">
        <span className="w-10">Lugar</span>
        <span className="w-12 text-center">Fatia</span>
        <span className="flex-1 text-right">Prêmio de hoje</span>
      </div>
      {payout.map((p) => (
        <div key={p.rank} data-testid={`oil-rank-prize-row-${p.rank}`}
          className={`flex items-center px-2 py-1 text-[10px] ${p.rank <= 3 ? 'bg-black/60 font-black text-amber-200' : 'bg-black/30 text-zinc-300'}`}>
          <span className="w-10">{p.rank}º</span>
          <span className="w-12 text-center text-amber-500">{p.pct}%</span>
          <span className="flex-1 text-right"><KakoCoin /> {fmt(Math.floor((pot * Number(p.pct)) / 100))}</span>
        </div>
      ))}
      <p className="bg-black/50 px-2 py-1 text-[8.5px] font-bold text-zinc-500">
        Previsão com o acumulado de agora (<KakoCoin /> {fmt(pot)}) — sobe a cada recarga do evento.
      </p>
    </div>
  );
  return createPortal(
    <div className="fixed inset-0 z-[97] flex items-center justify-center bg-black/90 backdrop-blur-sm"
      data-testid="oil-rank-rules-overlay" onClick={onClose}>
      <div className="w-full max-w-md p-3" onClick={(e) => e.stopPropagation()}>
        <div className="p-[2px]" style={{ background: 'linear-gradient(160deg, #f2d387, #6e5118 40%, #c99b3f 70%, #f2d387)', borderRadius: '18px' }}>
          <div className="overflow-hidden rounded-[16px] bg-[#0b0906] p-3.5">
            <div className="mb-1 flex items-center justify-between">
              <p className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.18em] text-amber-300">
                <img src="/oil/ic_regras.webp" alt="" className="h-7 w-7 object-contain" />
                {isMag ? 'Regras dos Magnatas' : 'Regras dos Corretores'}
              </p>
              <button onClick={() => { sfxClose(); onClose(); }} data-testid="oil-rank-rules-close"
                className="flex h-8 w-8 items-center justify-center rounded-full bg-black/70 text-sm font-black text-amber-300">✕</button>
            </div>

            <div className="max-h-[72vh] overflow-y-auto pr-0.5" data-testid="oil-rank-rules-body">
              {isMag ? (
                <>
                  <p className="rounded-xl border border-amber-900/50 bg-amber-950/20 p-3 text-[10px] italic leading-relaxed text-amber-100">
                    O <b className="text-amber-300">Ranking dos Magnatas</b> é a disputa principal da Bolsa
                    do Petróleo: quem construir o maior império em <b>FOX Dólar</b> até o fim da Season
                    leva a maior fatia do Jackpot.
                  </p>

                  <Sec n="1" title="Como você pontua" testid="oil-rank-rules-score">
                    <Li>A posição é o seu saldo de <b className="text-emerald-300"><OilIcon k="fox" /> FOX Dólar</b> — nada mais.</Li>
                    <Li>FOX Dólar vem de <b>vender barris</b>: você extrai gotas na Usina de Perfuração e
                      envasa nos barris da Envasadora.</Li>
                    <Li>Quanto maior o nível da Usina e da Envasadora, maiores os barris e mais FOX por gota.</Li>
                    <Li>O ranking é ao vivo: cada barril vendido pode te fazer subir ou cair de posição.</Li>
                  </Sec>

                  <Sec n="2" title="O que pode te derrubar" testid="oil-rank-rules-risk">
                    <Li>Golpes da aba Máfia <b>queimam</b> parte do seu FOX Dólar — o atacante não recebe nada,
                      o valor simplesmente desaparece do seu império.</Li>
                    <Li>Cada golpe pode tirar até 8% do seu saldo, com teto de 60.000 FOX por golpe e
                      no máximo 10% do seu FOX por dia.</Li>
                    <Li><b className="text-sky-300">Escudos</b> bloqueiam o golpe inteiro. Quem lidera o
                      ranking sem escudo é o alvo preferido de todo mundo.</Li>
                  </Sec>

                  <Sec n="3" title="Prêmio · Jackpot dos Magnatas" testid="oil-rank-rules-prize">
                    <Li>O prêmio é em <b className="text-amber-300">moedas KAKO de verdade</b>, acumuladas
                      durante toda a Season.</Li>
                    <Li>Acumulado agora: <b className="text-amber-300"><KakoCoin /> {fmt(jackpot.magnates)}</b> — cresce
                      a cada recarga feita no evento.</Li>
                    <Li>Dividido entre o <b>Top 10</b> em FOX Dólar no encerramento da Season:</Li>
                    {split}
                    <Li>Vale a posição do <b>fim</b> da Season. Liderar no meio do caminho não garante nada.</Li>
                    <Li>O crédito é processado pelo servidor após a conferência da administração,
                      uma única vez por ganhador.</Li>
                  </Sec>
                </>
              ) : (
                <>
                  <p className="rounded-xl border border-amber-900/50 bg-amber-950/20 p-3 text-[10px] italic leading-relaxed text-amber-100">
                    O <b className="text-amber-300">Ranking dos Corretores</b> é a disputa dos afiliados:
                    quem mais movimentar moedas na Bolsa durante a Season divide o Jackpot dos Corretores.
                  </p>

                  <Sec n="1" title="Quem entra neste ranking" testid="oil-rank-rules-who">
                    <Li>É exclusivo dos <b className="text-amber-300">afiliados</b> — não é ranking de jogador.</Li>
                    <Li>Você aparece aqui pelo seu cadastro de afiliado, não pelo nickname do jogo.</Li>
                  </Sec>

                  <Sec n="2" title="Como você pontua" testid="oil-rank-rules-score">
                    <Li>Toda recarga paga <b>pelo seu link de afiliado</b> soma as moedas daquela venda
                      na sua conta da Season.</Li>
                    <Li>A cada <b className="text-amber-300">{fmt(coinsPerAsset)} moedas vendidas</b> você
                      ganha <b>1 Ativo Financeiro</b> — é o Ativo que define a posição.</Li>
                    <Li>Venda sem link de afiliado não pontua para ninguém aqui.</Li>
                    <Li>Só entram vendas pagas: pedido cancelado ou não pago não conta.</Li>
                  </Sec>

                  <Sec n="3" title="Prêmio · Jackpot dos Corretores" testid="oil-rank-rules-prize">
                    <Li>O prêmio é em <b className="text-amber-300">moedas KAKO de verdade</b>, acumuladas
                      durante toda a Season.</Li>
                    <Li>Acumulado agora: <b className="text-amber-300"><KakoCoin /> {fmt(jackpot.shareholders)}</b> —
                      cresce a cada recarga feita no evento.</Li>
                    <Li>Dividido entre os <b>10 corretores</b> com mais Ativos no encerramento da Season:</Li>
                    {split}
                    <Li>O crédito é processado pelo servidor após a conferência da administração,
                      uma única vez por ganhador.</Li>
                  </Sec>

                  <Sec n="4" title="Bom saber" testid="oil-rank-rules-note">
                    <Li>A mesma recarga rende três coisas: <b>Moedas Pretas</b> para o cliente jogar,
                      <b> Jackpot</b> para os rankings e <b>Ativos</b> para o afiliado que trouxe a venda.</Li>
                    <Li>Sua comissão normal de afiliado continua igual — este ranking é um prêmio extra.</Li>
                  </Sec>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default OilRankRules;
